"use strict";
/* eslint-disable camelcase */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const lodash_isequal_1 = __importDefault(require("lodash.isequal"));
const prop_types_1 = __importDefault(require("prop-types"));
const window_or_global_1 = __importDefault(require("window-or-global"));
const styled_components_1 = __importDefault(require("styled-components"));
const YouTubeEmbedWrapper = styled_components_1.default.div `
    height: 100%;
    width: 100%;
    iframe{
        min-width: 100%;
        min-height: 100%;
    }
`;
/**
 * Privacy-enhanced mode uses youtube-nocookie.com.
 * @see {@link https://support.google.com/youtube/answer/171780?hl=en}
 */
const YOUTUBE = 'https://www.youtube-nocookie.com';
const STATE_CHANGE = 'onStateChange';
//const INFO_DELIVERY = 'onInfoDelivery';
const COMMAND = 'command';
const LISTENING = 'listening';
const CHANNEL = 'widget';
const READY = 'onReady';
const ACTIONS = {
    PLAY: 'playVideo',
    STOP: 'stopVideo',
    PAUSE: 'pauseVideo',
    SEEK: 'seekTo',
    MUTE: 'mute',
    UNMUTE: 'unMute',
    LOOP: 'setLoop'
};
const PLAYER_STATE = {
    UNSTARTED: -1,
    ENDED: 0,
    PLAYING: 1,
    PAUSED: 2,
    BUFFERING: 3,
    QUEUED: 5
};
/**
 * Function to interface with the iframe's player
 * @param {ACTIONS} type Action to send to the player
 * @param {Array} args arguments to send to the player
 */
const startListening = target => {
    if (!target)
        return;
    const message = JSON.stringify({
        event: LISTENING,
        id: target.playerId,
        channel: CHANNEL,
        origin: window_or_global_1.default.location.origin
    });
    target.contentWindow.postMessage(message, YOUTUBE);
    target.contentWindow.postMessage(JSON.stringify({
        event: COMMAND,
        func: 'addEventListener',
        args: [STATE_CHANGE],
        id: target.playerId,
        channel: CHANNEL,
        origin: window_or_global_1.default.location.origin
    }), YOUTUBE);
};
/**
 * Function to interface with the iframe's player
 * @param {ACTIONS} type Action to send to the player
 * @param {Array} args arguments to send to the player
 */
const sendCommand = (target, type, ...args) => {
    if (!target)
        return;
    const message = JSON.stringify({
        event: COMMAND,
        func: type,
        args: args || [],
        id: target.playerId,
        channel: CHANNEL,
        origin: window_or_global_1.default.location.origin
    });
    target.contentWindow.postMessage(message, YOUTUBE);
};
let widgetId = 1;
/**
 * YoutubeVideoEmbed.
 */
class YoutubeVideoEmbed extends react_1.default.Component {
    constructor() {
        super(...arguments);
        this.target = null;
        this.container = null;
        this.playerId = null;
        this.playerStatus = PLAYER_STATE.UNSTARTED;
        this.forceToPlay = true;
        this.listener = null;
        this.state = {
            videoOptions: {
                playerVars: {
                    autoplay: 0,
                    rel: 0,
                    fs: 1
                }
            }
        };
        this.componentWillUnmount = () => {
            if (this.listener) {
                window_or_global_1.default.removeEventListener('message', this.messageListener, true);
            }
        };
        // Code to listen for post messages of player status
        this.messageListener = message => {
            if (message.origin !== YOUTUBE)
                return;
            const data = JSON.parse(message.data);
            if (data.id !== this.playerId)
                return;
            if (data.event === READY) {
                this.onReady();
            }
            else if (data.event === STATE_CHANGE) {
                this.playerStatus = data.info;
                if (this.playerStatus === PLAYER_STATE.PLAYING) {
                    this.forceToPlay = false;
                }
                else if (this.playerStatus === PLAYER_STATE.UNSTARTED) {
                    this.checkPlay(this.props);
                }
            }
        };
        /**
         * Manually create and append the iframe so that each widget can have a unique ID.
         * This is required for when there are multiple frames on the same page.
         */
        this.componentDidMount = () => {
            const { video } = this.props;
            const iframe = window_or_global_1.default.document.createElement('iframe');
            this.playerId = widgetId++;
            iframe.id = `${video}-${this.playerId}`;
            iframe.className = 'youtube-video embed-responsive-item';
            iframe.frameBorder = '0';
            iframe.allowFullscreen = '1';
            iframe.title = 'YouTube video player';
            iframe.referrerPolicy = 'no-referrer';
            iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
            iframe.onload = this.onFrameLoaded.bind(this);
            window_or_global_1.default.addEventListener('message', this.messageListener, true);
            this.listener = true;
            this.container.appendChild(iframe);
        };
        this.checkVideoOptions = props => {
            if (props.videoOptions) {
                const options = props.videoOptions;
                options.playerVars.autoplay = props.play ? 1 : 0;
                options.playerVars.fs = props.launcher ? 0 : 1;
                if (!(0, lodash_isequal_1.default)(options, this.state.videoOptions)) {
                    this.setState({
                        videoOptions: options
                    });
                }
            }
        };
        this.muteVideo = () => {
            if (this.props.mute) {
                sendCommand(this.target, ACTIONS.MUTE);
            }
            else if (this.props.mute === false) {
                sendCommand(this.target, ACTIONS.UNMUTE);
            }
        };
        this.playVideo = () => {
            sendCommand(this.target, ACTIONS.LOOP, this.props.loop);
            if (this.props.play) {
                sendCommand(this.target, ACTIONS.PLAY);
            }
            setTimeout(() => {
                this.muteVideo();
            }, 50);
        };
        this.afterReady = this.playVideo;
        this.checkPlay = nextProps => {
            if (!this.target)
                return;
            if (this.props.play && !nextProps.play) {
                sendCommand(this.target, ACTIONS.PAUSE);
            }
            else if (nextProps.play) {
                //delay before playing again
                setTimeout(() => {
                    sendCommand(this.target, ACTIONS.PLAY);
                }, 50);
            }
        };
        this.onFrameLoaded = event => {
            this.target = event.target;
            // some videos become unplayable if the src is set before init
            if (!this.target.src) {
                this.target.src = this.getSrc();
            }
            this.target.playerId = this.playerId;
            startListening(this.target);
        };
        this.onReady = () => {
            if (this.afterReady && typeof this.afterReady === 'function') {
                this.afterReady();
            }
        };
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.target) {
            if (this.forceToPlay) {
                this.checkPlay(nextProps);
            }
        }
        else {
            this.afterReady = () => {
                this.checkPlay(nextProps);
            };
        }
        this.checkVideoOptions(nextProps);
    }
    UNSAFE_componentWillMount() {
        this.checkVideoOptions(this.props);
    }
    getSrc() {
        const { video } = this.props;
        const { autoplay, rel, fs, mute } = this.state.videoOptions.playerVars;
        const origin = encodeURIComponent(window_or_global_1.default.location.origin);
        const src = `${YOUTUBE}/embed/${video}?autoplay=${autoplay ||
            0}&rel=${rel}&fs=${fs}&enablejsapi=1&widgetid=${this.playerId}&mute=${mute}&origin=${origin}&enablejsapi=1`;
        return src;
    }
    shouldComponentUpdate(nextProps, nextState) {
        if (!(0, lodash_isequal_1.default)(nextState, this.state)) {
            return true;
        }
        const p = this.props;
        if (nextProps.video !== p.video ||
            nextProps.loop !== p.loop ||
            nextProps.mute !== p.mute ||
            nextProps.launcher !== p.launcher) {
            return true;
        }
        if (!(0, lodash_isequal_1.default)(nextProps.videoOptions, this.state.videoOptions)) {
            return true;
        }
        return false;
    }
    render() {
        return (react_1.default.createElement(YouTubeEmbedWrapper, { className: "youtube-video-embed embed-responsive embed-responsive-16by9", ref: c => {
                this.container = c;
            } }));
    }
}
exports.default = YoutubeVideoEmbed;
YoutubeVideoEmbed.propTypes = {
    video: prop_types_1.default.string,
    videoOptions: prop_types_1.default.object,
    play: prop_types_1.default.bool,
    mute: prop_types_1.default.bool,
    loop: prop_types_1.default.bool,
    launcher: prop_types_1.default.bool
};
//# sourceMappingURL=YoutubeVideoEmbed.js.map